import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardApi } from '../lib/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import {
    ClipboardCheck, Thermometer, AlertTriangle, TrendingUp,
    Plus, ChevronRight, CheckCircle2, Clock, RefreshCw
} from 'lucide-react';
import { formatDateTime, getTodayDate } from '../lib/utils';
import { toast } from 'sonner';

export default function Dashboard() {
    const { user, business, isManagerOrAdmin } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadDashboard(); }, []);

    const loadDashboard = async () => {
        try {
            const { data } = await dashboardApi.getStats();
            setStats(data);
        } catch {
            toast.error('Failed to load dashboard data');
        } finally { setLoading(false); }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    const completionRate = stats?.total_checklists_today > 0
        ? (stats.completed_checklists_today / stats.total_checklists_today) * 100 : 0;

    const statCards = [
        { label: 'Checklists Today', value: `${stats?.completed_checklists_today || 0}/${stats?.total_checklists_today || 0}`, icon: ClipboardCheck, color: 'text-blue-400 bg-blue-500/10' },
        { label: 'Temperature Logs', value: stats?.temperature_logs_today || 0, icon: Thermometer, color: 'text-emerald-400 bg-emerald-500/10', alert: stats?.temp_issues_today > 0 ? `${stats.temp_issues_today} issue(s)` : null },
        { label: 'Open Incidents', value: stats?.open_incidents || 0, icon: AlertTriangle, color: stats?.open_incidents > 0 ? 'text-rose-400 bg-rose-500/10' : 'text-slate-400 bg-slate-500/10' },
        { label: 'Compliance', value: `${Math.round(stats?.compliance_rate || 0)}%`, icon: TrendingUp, color: 'brand-icon brand-bg-subtle' },
    ];

    return (
        <Layout>
            <div className="space-y-6" data-testid="dashboard-page">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-100">
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, {user?.name?.split(' ')[0]}
                        </h1>
                        <p className="text-slate-400 text-sm mt-0.5">
                            {business ? business.name : 'Food safety overview for today'}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <Link to="/diary">
                            <Button size="sm" className="brand-btn font-medium" data-testid="new-checklist-button">
                                <Plus className="h-4 w-4 mr-1" /> New Checklist
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {statCards.map((s) => (
                        <div key={s.label} className="bg-slate-900/60 border border-slate-800 rounded-xl p-4" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-slate-400">{s.label}</span>
                                <div className={`p-1.5 rounded-lg ${s.color}`}><s.icon className="h-4 w-4" /></div>
                            </div>
                            <p className="text-xl font-bold text-slate-100">{s.value}</p>
                            {s.alert && <p className="text-xs text-rose-400 mt-1">{s.alert}</p>}
                        </div>
                    ))}
                </div>

                {/* Compliance Progress */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-base font-semibold text-slate-200">Today's Completion</h2>
                        <span className="text-sm brand-text font-semibold">{Math.round(completionRate)}%</span>
                    </div>
                    <div className="brand-progress-bar">
                        <Progress value={completionRate} className="h-2" />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">{stats?.pending_checklists || 0} pending checklists</p>
                </div>

                {/* Quick Actions + Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h2 className="text-base font-semibold text-slate-200 mb-3">Quick Actions</h2>
                        <div className="space-y-2">
                            {[
                                { to: '/diary', label: 'Complete daily checklist', icon: ClipboardCheck },
                                { to: '/temperature', label: 'Log temperature reading', icon: Thermometer },
                                { to: '/incidents', label: 'Report an incident', icon: AlertTriangle },
                                ...(isManagerOrAdmin ? [{ to: '/reports', label: 'View reports', icon: TrendingUp }] : []),
                            ].map((a) => (
                                <Link key={a.to} to={a.to}
                                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors text-slate-300 text-sm group">
                                    <span className="flex items-center gap-2"><a.icon className="h-4 w-4 text-slate-500" />{a.label}</span>
                                    <ChevronRight className="h-4 w-4 text-slate-600 group-hover:text-slate-300 transition-colors" />
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5">
                        <h2 className="text-base font-semibold text-slate-200 mb-3">Recent Activity</h2>
                        {stats?.recent_activity?.length > 0 ? (
                            <div className="space-y-2">
                                {stats.recent_activity.slice(0, 6).map((a, i) => (
                                    <div key={i} className="flex items-start gap-2.5 py-2 border-b border-slate-800/50 last:border-0">
                                        {a.type === 'completed' ? (
                                            <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                                        ) : (
                                            <Clock className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                                        )}
                                        <div className="min-w-0">
                                            <p className="text-sm text-slate-300 truncate">{a.description}</p>
                                            <p className="text-[11px] text-slate-500">{formatDateTime(a.timestamp)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-500 text-center py-6">No activity yet today</p>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
